import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useApp } from './AppContext';

// Initial state
const initialState = {
  items: [],
  recentlyRemoved: null,
  totalItems: 0,
  subtotal: 0,
  total: 0,
  isLoading: true, // Start with loading state
  isInitialized: false, // Track if cart has been loaded from storage
  isSubmittingOrder: false, // Track if order is being submitted
  specialInstructions: '',
  tableNumber: '',
  sessionId: null
};

// Action types
const actionTypes = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  UNDO_REMOVE: 'UNDO_REMOVE',
  CLEAR_RECENTLY_REMOVED: 'CLEAR_RECENTLY_REMOVED',
  SET_SPECIAL_INSTRUCTIONS: 'SET_SPECIAL_INSTRUCTIONS',
  SET_TABLE_NUMBER: 'SET_TABLE_NUMBER',
  SET_SESSION_ID: 'SET_SESSION_ID',
  SET_LOADING: 'SET_LOADING',
  SET_INITIALIZED: 'SET_INITIALIZED',
  SET_SUBMITTING_ORDER: 'SET_SUBMITTING_ORDER',
  CALCULATE_TOTALS: 'CALCULATE_TOTALS'
};

// Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.ADD_ITEM: {
      const { item, quantity = 1, customizations = {} } = action.payload;
      const existingItemIndex = state.items.findIndex(
        cartItem => 
          cartItem.id === item.id && 
          JSON.stringify(cartItem.customizations) === JSON.stringify(customizations)
      );

      let newItems;
      if (existingItemIndex >= 0) {
        // Item with same customizations exists, update quantity
        newItems = state.items.map((cartItem, index) =>
          index === existingItemIndex
            ? { ...cartItem, quantity: cartItem.quantity + quantity }
            : cartItem
        );
      } else {
        // New item or different customizations
        const cartItem = {
          id: item.id,
          name: item.name,
          price: item.price,
          image: item.image,
          // Small cart rows render this instead of the full image
          image_thumb: item.image_thumb,
          category: item.category,
          quantity,
          customizations,
          total: item.price * quantity
        };
        newItems = [...state.items, cartItem];
      }

      return {
        ...state,
        items: newItems
      };
    }

    case actionTypes.REMOVE_ITEM: {
      const { index, saveForUndo = true } = action.payload;
      const itemToRemove = state.items[index];
      const newItems = state.items.filter((_, i) => i !== index);
      
      return {
        ...state,
        items: newItems,
        recentlyRemoved: saveForUndo ? {
          item: itemToRemove,
          index,
          timestamp: Date.now()
        } : state.recentlyRemoved
      };
    }

    case actionTypes.UPDATE_QUANTITY: {
      const { index, quantity } = action.payload;
      if (quantity <= 0) {
        return cartReducer(state, { type: actionTypes.REMOVE_ITEM, payload: index });
      }

      const newItems = state.items.map((item, i) =>
        i === index
          ? { ...item, quantity, total: item.price * quantity }
          : item
      );

      return {
        ...state,
        items: newItems
      };
    }


    case actionTypes.UNDO_REMOVE: {
      if (!state.recentlyRemoved) return state;
      
      const { item, index } = state.recentlyRemoved;
      const newItems = [...state.items];
      newItems.splice(Math.min(index, newItems.length), 0, item);
      
      return {
        ...state,
        items: newItems,
        recentlyRemoved: null
      };
    }

    case actionTypes.CLEAR_RECENTLY_REMOVED:
      return {
        ...state,
        recentlyRemoved: null
      };

    case actionTypes.CLEAR_CART:
      return {
        ...initialState,
        isInitialized: true,
        isLoading: false,
        isSubmittingOrder: state.isSubmittingOrder, // Preserve submission state
        tableNumber: state.tableNumber,
        sessionId: state.sessionId
      };

    case actionTypes.SET_TABLE_NUMBER:
      return {
        ...state,
        tableNumber: action.payload
      };

    case actionTypes.SET_SESSION_ID:
      return {
        ...state,
        sessionId: action.payload
      };

    case actionTypes.SET_SPECIAL_INSTRUCTIONS:
      return {
        ...state,
        specialInstructions: action.payload
      };

    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case actionTypes.SET_INITIALIZED:
      return {
        ...state,
        isInitialized: true,
        isLoading: false
      };

    case actionTypes.SET_SUBMITTING_ORDER:
      return {
        ...state,
        isSubmittingOrder: action.payload
      };

    case actionTypes.CALCULATE_TOTALS: {
      const subtotal = state.items.reduce((sum, item) => sum + item.total, 0);
      const totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
      const total = subtotal; // No tax or fees for restaurant ordering

      return {
        ...state,
        totalItems,
        subtotal,
        total
      };
    }

    default:
      return state;
  }
};

// Create context
const CartContext = createContext();

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Cart Provider component
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { formatCurrency, addNotification } = useApp();

  // Load cart from localStorage on mount
  useEffect(() => {
    const initializeCart = async () => {
      const savedCart = localStorage.getItem('pizzainn_order_session');
      if (savedCart) {
        try {
          const cartData = JSON.parse(savedCart);
          // Restore cart items
          cartData.items?.forEach(item => {
            dispatch({
              type: actionTypes.ADD_ITEM,
              payload: {
                item: {
                  id: item.id,
                  name: item.name,
                  price: item.price,
                  image: item.image,
                  image_thumb: item.image_thumb,
                  category: item.category
                },
                quantity: item.quantity,
                customizations: item.customizations
              }
            });
          });
          
          // Restore session data
          if (cartData.tableNumber) {
            dispatch({
              type: actionTypes.SET_TABLE_NUMBER,
              payload: cartData.tableNumber
            });
          }
          
          if (cartData.sessionId) {
            dispatch({
              type: actionTypes.SET_SESSION_ID,
              payload: cartData.sessionId
            });
          }

          if (cartData.specialInstructions) {
            dispatch({
              type: actionTypes.SET_SPECIAL_INSTRUCTIONS,
              payload: cartData.specialInstructions
            });
          }
        } catch (error) {
          console.error('Error loading cart from localStorage:', error);
        }
      }
      
      // Mark cart as initialized
      dispatch({ type: actionTypes.SET_INITIALIZED });
    };

    initializeCart();
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    const cartData = {
      items: state.items,
      tableNumber: state.tableNumber,
      sessionId: state.sessionId,
      specialInstructions: state.specialInstructions,
      timestamp: Date.now()
    };
    localStorage.setItem('pizzainn_order_session', JSON.stringify(cartData));
  }, [state.items, state.tableNumber, state.sessionId, state.specialInstructions]);

  // Recalculate totals whenever items change
  useEffect(() => {
    dispatch({ type: actionTypes.CALCULATE_TOTALS });
  }, [state.items]);

  // Add item to cart
  const addItem = (item, quantity = 1, customizations = {}) => {
    dispatch({
      type: actionTypes.ADD_ITEM,
      payload: { item, quantity, customizations }
    });

    addNotification({
      type: 'success',
      message: `${item.name} added to cart`,
      duration: 2000
    });
  };

  // Remove item from cart
  const removeItem = (index, saveForUndo = true) => {
    const item = state.items[index];
    dispatch({
      type: actionTypes.REMOVE_ITEM,
      payload: { index, saveForUndo }
    });

    if (saveForUndo) {
      addNotification({
        type: 'info',
        message: `${item.name} removed from cart`,
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () => undoRemove()
        }
      });

      // Auto-clear undo after 10 seconds
      setTimeout(() => {
        dispatch({ type: actionTypes.CLEAR_RECENTLY_REMOVED });
      }, 10000);
    } else {
      addNotification({
        type: 'info',
        message: `${item.name} removed from cart`,
        duration: 2000
      });
    }
  };

  // Update item quantity
  const updateQuantity = (index, quantity) => {
    dispatch({
      type: actionTypes.UPDATE_QUANTITY,
      payload: { index, quantity }
    });
  };

  // Clear entire cart
  const clearCart = () => {
    dispatch({ type: actionTypes.CLEAR_CART });
    addNotification({
      type: 'info',
      message: 'Order cleared',
      duration: 2000
    });
  };

  // These four only ever call dispatch, which React guarantees is stable for
  // the lifetime of the reducer. They were plain arrow functions, so their
  // identity changed on every provider render -- and because the reducer
  // returns a fresh object for every action, a consumer that lists one of them
  // in a useEffect dependency array loops:
  //
  //   render -> new setter identity -> effect re-runs -> dispatch
  //          -> new state object -> render -> ...
  //
  // Checkout.js did exactly that with setTableNumber and
  // setSpecialInstructions, spinning tightly on the checkout page while the
  // customer was trying to pay. Stable identities break the cycle at its
  // source, so every present and future consumer is safe.
  const setTableNumber = useCallback((tableNumber) => {
    dispatch({
      type: actionTypes.SET_TABLE_NUMBER,
      payload: tableNumber
    });
  }, []);

  // Set session ID
  const setSessionId = useCallback((sessionId) => {
    dispatch({
      type: actionTypes.SET_SESSION_ID,
      payload: sessionId
    });
  }, []);

  const setSpecialInstructions = useCallback((instructions) => {
    dispatch({
      type: actionTypes.SET_SPECIAL_INSTRUCTIONS,
      payload: instructions
    });
  }, []);

  const setSubmittingOrder = useCallback((isSubmitting) => {
    dispatch({
      type: actionTypes.SET_SUBMITTING_ORDER,
      payload: isSubmitting
    });
  }, []);

  // Get item in cart by ID and customizations
  const getCartItem = (itemId, customizations = {}) => {
    return state.items.find(
      item => 
        item.id === itemId && 
        JSON.stringify(item.customizations) === JSON.stringify(customizations)
    );
  };

  // Check if item is in cart
  const isInCart = (itemId, customizations = {}) => {
    return !!getCartItem(itemId, customizations);
  };

  // Get total quantity of specific item in cart
  const getItemQuantity = (itemId) => {
    return state.items
      .filter(item => item.id === itemId)
      .reduce((total, item) => total + item.quantity, 0);
  };

  // Validate order before placement
  const validateOrder = () => {
    if (state.items.length === 0) {
      return { isValid: false, message: 'Your order is empty' };
    }

    return { isValid: true };
  };

  // Undo last remove action
  const undoRemove = () => {
    if (state.recentlyRemoved) {
      const item = state.recentlyRemoved.item;
      dispatch({ type: actionTypes.UNDO_REMOVE });

      addNotification({
        type: 'success',
        message: `${item.name} restored to cart`,
        duration: 2000
      });
    }
  };

  // Clear recently removed item (disable undo)
  const clearRecentlyRemoved = () => {
    dispatch({ type: actionTypes.CLEAR_RECENTLY_REMOVED });
  };

  // Get order summary for submission
  const getOrderSummary = () => {
    return {
      items: state.items.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        customizations: item.customizations,
        total: item.total
      })),
      tableNumber: state.tableNumber,
      sessionId: state.sessionId,
      specialInstructions: state.specialInstructions,
      subtotal: state.subtotal,
      total: state.total,
      totalItems: state.totalItems
    };
  };

  const value = {
    // State
    ...state,
    
    // Actions
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    undoRemove,
    clearRecentlyRemoved,
    setTableNumber,
    setSessionId,
    setSpecialInstructions,
    setSubmittingOrder,
    
    // Utilities
    getCartItem,
    isInCart,
    getItemQuantity,
    validateOrder,
    getOrderSummary,
    formatCurrency
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
